import { Link } from "react-router-dom";
import { MapPin, CircleDot } from "lucide-react";
import { media, fmtPrice } from "@/lib/api";
import { useVisitor } from "@/lib/visitor";
import { AvailableBadge, DemoTag, VerifiedBadge } from "./Badges";
import SaveButton from "./SaveButton";

export default function ProfileCard({ card, index = 0, showCity = false }) {
  const { blur } = useVisitor();
  const price = fmtPrice(card.price);
  return (
    <Link
      to={`/profile/${card.slug}`}
      data-testid="results-profile-card"
      className="group relative block overflow-hidden rounded-[12px] border border-lux-border bg-lux-surface shadow-luxSoft transition-colors duration-200 hover:border-lux-silver/45 animate-lux-fade-up"
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
    >
      <div className="lux-card-media relative aspect-[3/4] overflow-hidden bg-lux-surface2">
        {card.cover && (
          <img
            src={media(card.cover)}
            alt={card.is_demo ? `Demo profile photo for ${card.display_name}` : `Photo of ${card.display_name}`}
            loading={index < 4 ? "eager" : "lazy"}
            decoding="async"
            className={`h-full w-full object-cover ${blur ? "blur-2xl group-hover:blur-0 group-focus-visible:blur-0" : ""}`}
            data-blurred={blur ? "true" : "false"}
          />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-black/25" />
        <div className="absolute left-2.5 top-2.5 flex items-center gap-1.5">
          {card.is_demo && <DemoTag testId="results-profile-card-demo-tag" />}
        </div>
        <div className="absolute right-2.5 top-2.5 flex flex-col items-end gap-2">
          {card.available_today && <AvailableBadge testId="results-profile-card-available-badge" />}
          <SaveButton slug={card.slug} name={card.display_name} testId="results-profile-card-save-button" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
          <div className="flex items-baseline gap-2">
            <h3 className="font-serif text-[22px] leading-none text-lux-text sm:text-2xl">{card.display_name}</h3>
            <span className="text-sm text-lux-silver">{card.age}</span>
            {card.has_story && (
              <CircleDot className="ml-auto h-3.5 w-3.5 text-lux-silverBright" aria-label="Has a story today" />
            )}
          </div>
          <p className="mt-1.5 flex items-center gap-1 truncate text-xs text-lux-muted">
            <MapPin className="h-3 w-3 shrink-0 text-lux-silver" aria-hidden="true" />
            <span className="truncate">{card.primary_area?.name}{showCity && card.city?.name ? ` · ${card.city.name}` : ""}</span>
          </p>
          <div className="mt-2.5 flex items-center justify-between gap-2">
            <VerifiedBadge days={card.verified_days_ago} testId="results-profile-card-verified-badge" />
            {price && (
              <span className="shrink-0 rounded-full border border-lux-border bg-black/55 px-2.5 py-1 text-xs font-medium text-lux-silverBright backdrop-blur" data-testid="results-profile-card-price">
                {price}
              </span>
            )}
          </div>
        </div>
      </div>
      <span className="sr-only" data-testid="results-profile-card-open">Open profile</span>
    </Link>
  );
}
